import React, { useEffect, useState } from "react";
import { api } from "../services/api";
import Swal from "sweetalert2";

interface CategoriaProduto {
  id: number;
  nome: string;
}

export interface Produto {
  id?: number;
  nome: string;
  precoCusto: number;
  precoVenda: number;
  categoriaProdutoId: number;
  quantidadeEstoque: number;
  ativo: boolean;
  imagemUrl?: string | null;
}

interface ProdutoFormProps {
  produto?: Produto;
  onSave: () => void;
  onCancel: () => void;
}

/* -------------------------------
   MÁSCARA DE MOEDA (EXIBIÇÃO)
-------------------------------- */
const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ProdutoForm: React.FC<ProdutoFormProps> = ({ produto, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Produto>({
    nome: "",
    precoCusto: 0,
    precoVenda: 0,
    categoriaProdutoId: 0,
    quantidadeEstoque: 0,
    ativo: true,
    imagemUrl: "",
  });

  // Estados de exibição da máscara
  const [displayPrecoCusto, setDisplayPrecoCusto] = useState("0,00");
  const [displayPrecoVenda, setDisplayPrecoVenda] = useState("0,00");

  const [imagemPreview, setImagemPreview] = useState<string | null>(null);
  const [imagemFile, setImagemFile] = useState<File | null>(null);
  const [categorias, setCategorias] = useState<CategoriaProduto[]>([]);
  const [loading, setLoading] = useState(false);

  /* -------------------------------
      Carregar categorias
  -------------------------------- */
  useEffect(() => {
    const carregarCategorias = async () => {
      try {
        const response = await api.get("/CategoriaProduto");
        setCategorias(response.data);
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
      }
    };
    carregarCategorias();
  }, []);

  /* -------------------------------
      Carregar produto (edição)
  -------------------------------- */
  useEffect(() => {
    if (produto) {
      setFormData(produto);

      setDisplayPrecoCusto(formatCurrency(produto.precoCusto));
      setDisplayPrecoVenda(formatCurrency(produto.precoVenda));

      if (produto.imagemUrl) {
        const url =
          produto.imagemUrl.startsWith("http") || produto.imagemUrl.startsWith("/uploads")
            ? produto.imagemUrl
            : `https://localhost:7164/uploads/produtos/${produto.imagemUrl}`;
        setImagemPreview(url);
      }
    }
  }, [produto]);

  /* -------------------------------
      Handle Change com máscara PRO
  -------------------------------- */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;

    // --- MÁSCARA DE MOEDA ---
    if (name === "precoCusto" || name === "precoVenda") {
      let onlyNumbers = value.replace(/\D/g, "");
      if (!onlyNumbers) onlyNumbers = "0";

      const numeric = Number(onlyNumbers) / 100;

      const formatted = numeric.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      if (name === "precoCusto") setDisplayPrecoCusto(formatted);
      if (name === "precoVenda") setDisplayPrecoVenda(formatted);

      setFormData((prev) => ({
        ...prev,
        [name]: numeric,
      }));

      return;
    }

    // Checkbox
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: target.checked,
      }));
      return;
    }

    // Campos numéricos
    if (["quantidadeEstoque", "categoriaProdutoId"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: Number(value),
      }));
      return;
    }

    // Texto normal
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* -------------------------------
      Handle imagem
  -------------------------------- */
  const handleImagemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImagemFile(file);
      setImagemPreview(URL.createObjectURL(file));
    }
  };

  /* -------------------------------
      Confirmar ação
  -------------------------------- */
  const confirmarAcao = async (): Promise<boolean> => {
    const resultado = await Swal.fire({
      title: formData.id ? "Confirmar atualização" : "Confirmar cadastro",
      text: formData.id
        ? "Deseja realmente atualizar este produto?"
        : "Deseja realmente cadastrar este novo produto?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, confirmar",
      cancelButtonText: "Cancelar",
    });
    return resultado.isConfirmed;
  };

  /* -------------------------------
      Submit do formulário
  -------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const confirmado = await confirmarAcao();
    if (!confirmado) return;

    setLoading(true);

    try {
      let imagemUrl = formData.imagemUrl;

      if (imagemFile) {
        const imgData = new FormData();
        imgData.append("file", imagemFile);

        const uploadResponse = await api.post("/Produto/Upload/Produto", imgData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        imagemUrl = uploadResponse.data.url;
      }

      const payload = { ...formData, imagemUrl };

      if (formData.id) {
        await api.put(`/Produto/${formData.id}`, payload);
        Swal.fire("Sucesso!", "Produto atualizado com sucesso!", "success");
      } else {
        await api.post("/Produto", payload);
        Swal.fire("Sucesso!", "Produto cadastrado com sucesso!", "success");
      }

      onSave();
    } catch (error) {
      console.error("Erro ao salvar produto:", error);
      Swal.fire("Erro", "Não foi possível salvar o produto.", "error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
      Render
  -------------------------------- */
  return (
    <div className="card p-4 shadow-sm">
      <h5 className="mb-3">{formData.id ? "Editar Produto" : "Cadastrar Novo Produto"}</h5>

      <form onSubmit={handleSubmit} className="row g-3">

        <div className="col-md-6">
          <label className="form-label">Nome</label>
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Preço de Custo</label>
          <input
            type="text"
            name="precoCusto"
            value={displayPrecoCusto}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Preço de Venda</label>
          <input
            type="text"
            name="precoVenda"
            value={displayPrecoVenda}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Categoria</label>
          <select
            name="categoriaProdutoId"
            value={formData.categoriaProdutoId}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value={0}>Selecione uma categoria</option>
            {categorias.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Quantidade em Estoque</label>
          <input
            type="number"
            name="quantidadeEstoque"
            value={formData.quantidadeEstoque}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-3 d-flex align-items-center">
          <div className="form-check mt-4">
            <input
              className="form-check-input"
              type="checkbox"
              name="ativo"
              checked={formData.ativo}
              onChange={handleChange}
              id="ativoCheck"
            />
            <label className="form-check-label" htmlFor="ativoCheck">
              Produto ativo
            </label>
          </div>
        </div>

        <div className="col-md-6">
          <label className="form-label">Imagem do Produto</label>
          <input type="file" accept="image/*" className="form-control" onChange={handleImagemChange} />
          {imagemPreview && (
            <div className="mt-2 text-center">
              <img
                src={imagemPreview}
                alt="Pré-visualização"
                className="img-thumbnail mt-2"
                style={{ maxWidth: "150px" }}
              />
            </div>
          )}
        </div>

        <div className="col-12 d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Salvando..." : formData.id ? "Atualizar" : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProdutoForm;
